import React from 'react';
import { 
  Checkbox, 
  FormControlLabel, 
  FormControl, 
  FormHelperText, 
  FormGroup,
  Switch
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual experience
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: 0,
  },
}));

/**
 * MagicCheckbox - Enhanced checkbox/switch component with consistent styling
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {boolean} props.checked - Whether the checkbox is checked
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {boolean} props.isSwitch - Whether to render as a switch instead of checkbox
 * @param {Object} props.checkboxProps - Additional props for the Checkbox/Switch
 * @param {Object} props.labelProps - Additional props for the FormControlLabel
 * @param {Object} props.formControlProps - Additional props for the FormControl
 */
const MagicCheckbox = ({
  name,
  label,
  checked = false,
  onChange,
  error = false,
  helperText = '',
  isSwitch = false,
  checkboxProps = {},
  labelProps = {},
  formControlProps = {},
  ...props
}) => {
  // Handle change event
  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.checked, name);
    }
  };

  // Choose component based on isSwitch prop
  const ToggleComponent = isSwitch ? Switch : Checkbox;

  return (
    <StyledFormControl 
      error={error} 
      component="fieldset" 
      {...formControlProps}
    >
      <FormGroup>
        <FormControlLabel
          control={
            <ToggleComponent
              checked={checked}
              onChange={handleChange}
              name={name}
              {...checkboxProps}
            />
          }
          label={label}
          {...labelProps}
        />
      </FormGroup>
      
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default MagicCheckbox;